import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { VideoPlayer } from "@/components/VideoPlayer";
import { Plus, PencilSimple, Trash, Spinner, VideoCamera, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { title: "", description: "", category: "General", url: "", poster: "", order: 0 };
const CHUNK = 4 * 1024 * 1024;

async function uploadVideoFile(file, onProgress) {
  const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, "0")).join("");
  const total = Math.ceil(file.size / CHUNK);
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", String(i));
    fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), `${i}.part`);
    await api.post("/admin/videos/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
    onProgress(Math.round(((i + 1) / total) * 95));
  }
  const { data } = await api.post("/admin/videos/upload/complete", { upload_id: uploadId, filename: file.name, total_chunks: total });
  onProgress(100);
  return data.url;
}

export default function AdminVideos() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) { toast.error("Video must be under 500 MB"); return; }
    setProgress(0);
    try {
      const url = await uploadVideoFile(file, setProgress);
      setEditing((s) => ({ ...s, url, title: s.title || file.name.replace(/\.[^.]+$/, "") }));
      toast.success("Video uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    setProgress(null);
  };

  const load = () => { setLoading(true); api.get("/videos").then(({ data }) => { setRows(data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);

  if (user.role !== "super_admin") return <div className="p-8 text-zinc-500" data-testid="videos-admin-denied">Super admin access only.</div>;

  const save = async () => {
    if (!editing.title.trim() || !editing.url.trim()) { toast.error("Title and video URL are required"); return; }
    setSaving(true);
    try {
      const body = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/videos/${editing.id}`, body); else await api.post("/admin/videos", body);
      toast.success(editing.id ? "Video updated" : "Video added");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const del = async (v) => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    try { await api.delete(`/admin/videos/${v.id}`); setRows((rs) => rs.filter((x) => x.id !== v.id)); toast.success("Video deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const set = (k) => (e) => setEditing((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div className="space-y-4" data-testid="admin-videos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Videos</h1>
          <p className="text-zinc-500 text-sm mt-1">{rows.length} published on the public <a href="/videos" target="_blank" rel="noreferrer" className="text-emerald-700 font-medium hover:underline">/videos</a> page</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY, order: rows.length })} className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold gap-2" data-testid="video-add-button"><Plus size={18} weight="bold" />Add Video</Button>
      </div>

      {loading ? <div className="py-20 text-center"><Spinner size={28} className="animate-spin text-emerald-600 mx-auto" /></div>
        : rows.length === 0 ? <div className="rounded-lg border border-zinc-200 bg-white py-20 text-center text-zinc-400" data-testid="videos-admin-empty"><VideoCamera size={32} className="mx-auto mb-2 text-zinc-300" />No videos yet. Add your first one.</div>
        : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {rows.map((v) => (
              <div key={v.id} className="rounded-lg border border-zinc-200 bg-white overflow-hidden" data-testid={`admin-video-${v.id}`}>
                <div className="aspect-video bg-black"><VideoPlayer video={v} /></div>
                <div className="p-4">
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-emerald-600">{v.category || "General"} · #{v.order}</p>
                  <h3 className="font-display font-bold mt-1 leading-snug">{v.title}</h3>
                  {v.description && <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{v.description}</p>}
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" className="border-zinc-300 gap-1.5" onClick={() => setEditing({ ...EMPTY, ...v })} data-testid={`video-edit-${v.id}`}><PencilSimple size={14} />Edit</Button>
                    <Button size="sm" variant="outline" className="border-zinc-300 text-red-600 hover:text-red-700 gap-1.5" onClick={() => del(v)} data-testid={`video-delete-${v.id}`}><Trash size={14} />Delete</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="video-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Video" : "Add Video"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={editing.title} onChange={set("title")} placeholder="One Scan on the Lot" data-testid="video-title-input" /></div>
              <div>
                <Label>Video</Label>
                <div className="mt-1 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-3">
                  <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-zinc-950 text-white text-sm font-semibold cursor-pointer hover:bg-zinc-800" data-testid="video-upload-label">
                    <UploadSimple size={16} weight="bold" />{progress === null ? "Upload video file" : `Uploading… ${progress}%`}
                    <input type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v" className="hidden" onChange={onFile} disabled={progress !== null} data-testid="video-file-input" />
                  </label>
                  {progress !== null && <div className="h-1.5 rounded bg-zinc-200 mt-2"><div className="h-full rounded bg-emerald-500 transition-all" style={{ width: `${progress}%` }} data-testid="video-upload-progress" /></div>}
                  <p className="text-xs text-zinc-500 mt-2">MP4, MOV, WEBM up to 500 MB — or paste a hosted / YouTube / Vimeo link below.</p>
                </div>
                <Input className="mt-2" value={editing.url} onChange={set("url")} placeholder="https://… .mp4, YouTube or Vimeo link" data-testid="video-url-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label><Input value={editing.category} onChange={set("category")} placeholder="Auto Dealerships" data-testid="video-category-input" /></div>
                <div><Label>Order</Label><Input type="number" value={editing.order} onChange={set("order")} data-testid="video-order-input" /></div>
              </div>
              <div><Label>Description</Label><Textarea rows={3} value={editing.description} onChange={set("description")} placeholder="What this scenario shows…" data-testid="video-description-input" /></div>
              <div><Label>Poster image URL <span className="text-zinc-400 font-normal">(optional, mp4 only)</span></Label><Input value={editing.poster} onChange={set("poster")} data-testid="video-poster-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-zinc-300" onClick={() => setEditing(null)} data-testid="video-cancel-button">Cancel</Button>
            <Button onClick={save} disabled={saving || progress !== null} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="video-save-button">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
